/**
 * The run every result surface should actually score.
 *
 * The provider's state is this device's copy of the walk. When a learner opens
 * their result or Passport on another device — or after local state was
 * cleared, or when the run finished before the cloud mirror synced — that copy
 * can be an empty shell. Scoring the shell produces a report that looks real
 * and is not: every archetype bar sits at zero, the primary falls back to the
 * first name in the canonical list, and nothing clears the Best-match floor.
 *
 * So: use the local state when it carries a real Part 2 tally, otherwise
 * replay the durable response log and score that instead.
 */
import { useEffect, useState } from "react";
import { loadLatestCompletedRun } from "./persistedSnapshot";
import type { AssessmentV2State } from "./types";
import type { Band } from "./band";

/** Sum of the revealed-decision tally. Zero means "no Part 2 evidence here". */
export function runTallyTotal(state: { part2?: Record<string, unknown> } | null): number {
  const tally = state?.part2?.archetype_tally as Record<string, unknown> | undefined;
  if (!tally || typeof tally !== "object") return 0;
  return Object.values(tally).reduce<number>((n, v) => n + (Number(v) || 0), 0);
}

export interface EffectiveRun {
  state: AssessmentV2State;
  /** True while we're still checking the durable log for a better copy. */
  loading: boolean;
  /** True when the state came from the response log, not this device. */
  fromCloud: boolean;
}

export function useEffectiveRunState(local: AssessmentV2State, band: Band): EffectiveRun {
  const localHasEvidence = runTallyTotal(local) > 0;
  const [replay, setReplay] = useState<AssessmentV2State | null>(null);
  const [loading, setLoading] = useState(!localHasEvidence);

  useEffect(() => {
    if (localHasEvidence) {
      setReplay(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const run = await loadLatestCompletedRun();
        if (cancelled) return;
        const candidate = run?.state ?? null;
        setReplay(candidate && runTallyTotal(candidate) > 0 ? candidate : null);
      } catch {
        if (!cancelled) setReplay(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [localHasEvidence, band]);

  if (localHasEvidence || !replay) {
    return { state: local, loading: localHasEvidence ? false : loading, fromCloud: false };
  }
  // Keep the band the surface was opened with; the replay only supplies answers.
  return { state: { ...replay, band: local.band }, loading: false, fromCloud: true };
}
